import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';

// Types
export interface ScheduleVersion {
  id: string;
  scheduleId: string;
  versionNumber: number;
  name: string;
  startDate: string;
  endDate: string;
  status: string;
  sourceType: string;
  sourceForecastId?: string;
  publishedAt?: string;
  createdBy?: string;
  createdAt: string;
  changeReason?: string;
  changeType: string;
  snapshot: any;
  shiftSnapshot: any[];
  changes?: {
    schedule: Record<string, { old: any; new: any }>;
    shifts: {
      added: any[];
      removed: any[];
      modified: Array<{ id: string; changes: Record<string, { old: any; new: any }> }>;
    };
  };
}

export interface VersionComparison {
  version1: {
    versionNumber: number;
    createdAt: string;
    createdBy?: string;
    changeType: string;
  };
  version2: {
    versionNumber: number;
    createdAt: string;
    createdBy?: string;
    changeType: string;
  };
  changes: {
    schedule: Record<string, { old: any; new: any }>;
    shifts: {
      added: any[];
      removed: any[];
      modified: Array<{ id: string; changes: Record<string, { old: any; new: any }> }>;
    };
  };
}

// API Service
const scheduleVersionsApi = {
  getVersions: async (scheduleId: string): Promise<ScheduleVersion[]> => {
    const response = await api.get(`/schedules/${scheduleId}/versions`);
    return response.data;
  },

  getVersion: async (scheduleId: string, versionNumber: number): Promise<ScheduleVersion> => {
    const response = await api.get(`/schedules/${scheduleId}/versions/${versionNumber}`);
    return response.data;
  },

  createVersion: async (scheduleId: string, data: {
    changeType: string;
    changeReason?: string;
    createdBy?: string;
  }): Promise<ScheduleVersion> => {
    const response = await api.post(`/schedules/${scheduleId}/versions`, data);
    return response.data;
  },

  compareVersions: async (
    scheduleId: string,
    version1: number,
    version2: number,
  ): Promise<VersionComparison> => {
    const response = await api.get(
      `/schedules/${scheduleId}/versions/compare/${version1}/${version2}`,
    );
    return response.data;
  },

  restoreVersion: async (
    scheduleId: string,
    versionNumber: number,
    restoredBy?: string,
  ): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(
      `/schedules/${scheduleId}/versions/${versionNumber}/restore`,
      { restoredBy },
    );
    return response.data;
  },
};

// React Query Hooks

/**
 * Get all versions for a schedule
 */
export function useScheduleVersions(scheduleId?: string) {
  return useQuery({
    queryKey: ['schedule-versions', scheduleId],
    queryFn: () => scheduleVersionsApi.getVersions(scheduleId!),
    enabled: !!scheduleId,
  });
}

/**
 * Get a specific version
 */
export function useScheduleVersion(scheduleId?: string, versionNumber?: number) {
  return useQuery({
    queryKey: ['schedule-versions', scheduleId, versionNumber],
    queryFn: () => scheduleVersionsApi.getVersion(scheduleId!, versionNumber!),
    enabled: !!scheduleId && !!versionNumber,
  });
}

/**
 * Create a new version
 */
export function useCreateScheduleVersion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      scheduleId,
      data,
    }: {
      scheduleId: string;
      data: {
        changeType: string;
        changeReason?: string;
        createdBy?: string;
      };
    }) => scheduleVersionsApi.createVersion(scheduleId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['schedule-versions', variables.scheduleId] });
      queryClient.invalidateQueries({ queryKey: ['schedules', variables.scheduleId] });
    },
  });
}

/**
 * Compare two versions
 */
export function useCompareScheduleVersions(
  scheduleId?: string,
  version1?: number,
  version2?: number,
) {
  return useQuery({
    queryKey: ['schedule-versions-compare', scheduleId, version1, version2],
    queryFn: () => scheduleVersionsApi.compareVersions(scheduleId!, version1!, version2!),
    enabled: !!scheduleId && !!version1 && !!version2,
  });
}

/**
 * Restore a version
 */
export function useRestoreScheduleVersion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      scheduleId,
      versionNumber,
      restoredBy,
    }: {
      scheduleId: string;
      versionNumber: number;
      restoredBy?: string;
    }) => scheduleVersionsApi.restoreVersion(scheduleId, versionNumber, restoredBy),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['schedule-versions', variables.scheduleId] });
      queryClient.invalidateQueries({ queryKey: ['schedules', variables.scheduleId] });
      queryClient.invalidateQueries({ queryKey: ['shifts'] });
    },
  });
}
